import { on } from "@ngrx/store";
import { ContractActions } from "./contract.actions";
import { ContractsActions } from "./contracts/contracts.actions";
import { ContractManagementActions } from "../admin/contract-admin/contract-management/contract-management.actions";
import { contractsReducer } from "./contracts/contracts.reducer";
import { ContractState, initialContractState } from "./contract.state";
import { PagesStore } from "../pages.state";

// **************************************************************************
// HELPER: Update contract state within PagesStore
// **************************************************************************

function withContract(state: PagesStore, updater: (contract: ContractState) => ContractState): PagesStore {
    return { ...state, contract: updater(state.contract) };
}

// **************************************************************************
// CONTRACT REDUCER (array format for PagesStore)
// **************************************************************************

export const contractReducer = [
    // *** DETAIL ***
    on(ContractActions.loadContractSuccess, (state: PagesStore, { contract }) => withContract(state, contractState => ({
        ...contractState,
        contract,
        error: null,
    }))),

    on(ContractActions.loadContractFailure, (state: PagesStore, { error }) => withContract(state, contractState => ({
        ...contractState,
        error,
    }))),

    // *** UPDATE ***
    on(ContractActions.updateContractSuccess, (state: PagesStore, { contract }) => withContract(state, contractState => ({
        ...contractState,
        contract,
        error: null,
    }))),

    on(ContractActions.updateContractFailure, (state: PagesStore, { error }) => withContract(state, contractState => ({
        ...contractState,
        error,
    }))),

    // *** SECTIONS ***
    on(
        ContractActions.createSectionSuccess,
        (state: PagesStore, { section }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractSections: [...(contractState.contract.contractSections ?? []), section],
            } : null,
        })),
    ),

    on(
        ContractActions.updateSectionSuccess,
        ContractActions.setSectionCatalogsSuccess,
        (state: PagesStore, { section }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractSections: (contractState.contract.contractSections ?? []).map(s => (s.id === section.id ? section : s)),
            } : null,
        })),
    ),

    on(
        ContractActions.deleteSectionSuccess,
        (state: PagesStore, { sectionId }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractSections: (contractState.contract.contractSections ?? []).filter(s => s.id !== sectionId),
            } : null,
        })),
    ),

    // *** FILES ***
    on(
        ContractActions.uploadFilesSuccess,
        (state: PagesStore, { files }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractFiles: [...(contractState.contract.contractFiles ?? []), ...files],
            } : null,
        })),
    ),

    on(
        ContractActions.deleteFileSuccess,
        (state: PagesStore, { fileId }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractFiles: (contractState.contract.contractFiles ?? []).filter(f => f.id !== fileId),
            } : null,
        })),
    ),

    // *** REMINDERS ***
    on(
        ContractActions.loadRemindersSuccess,
        (state: PagesStore, { reminders }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractReminders: reminders,
            } : null,
        })),
    ),

    on(
        ContractActions.createReminderSuccess,
        (state: PagesStore, { reminder }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractReminders: [...(contractState.contract.contractReminders ?? []), reminder],
            } : null,
        })),
    ),

    on(
        ContractActions.updateReminderSuccess,
        (state: PagesStore, { reminder }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractReminders: (contractState.contract.contractReminders ?? []).map(r => (r.id === reminder.id ? reminder : r)),
            } : null,
        })),
    ),

    on(
        ContractActions.deleteReminderSuccess,
        (state: PagesStore, { reminderId }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractReminders: (contractState.contract.contractReminders ?? []).filter(r => r.id !== reminderId),
            } : null,
        })),
    ),

    // *** DISCOUNT MODE ***
    on(
        ContractActions.setDiscountTypeSuccess,
        (state: PagesStore, { contract }) => withContract(state, contractState => ({
            ...contractState,
            contract,
        })),
    ),

    // *** DISCOUNTS ***
    on(
        ContractActions.createDiscountSuccess,
        (state: PagesStore, { discount }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractDiscounts: [...(contractState.contract.contractDiscounts ?? []), discount],
            } : null,
        })),
    ),

    on(
        ContractActions.updateDiscountSuccess,
        (state: PagesStore, { discount }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractDiscounts: (contractState.contract.contractDiscounts ?? []).map(d => (d.id === discount.id ? discount : d)),
            } : null,
        })),
    ),

    on(
        ContractActions.deleteDiscountSuccess,
        (state: PagesStore, { discountId }) => withContract(state, contractState => ({
            ...contractState,
            contract: contractState.contract ? {
                ...contractState.contract,
                contractDiscounts: (contractState.contract.contractDiscounts ?? []).filter(d => d.id !== discountId),
            } : null,
        })),
    ),

    // *** RESET ***
    // Only reset detail state, preserve list data (contracts, loaded, filters)
    on(ContractActions.resetState, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        contract: null,
        error: null,
    }))),

    // **************************************************************************
    // LOADING HANDLERS
    // **************************************************************************

    // List loading
    on(ContractsActions.load, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingList: true,
    }))),
    on(ContractsActions.loadSuccess, ContractsActions.loadFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingList: false,
    }))),

    // Detail loading
    on(ContractActions.loadContract, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingDetail: true,
    }))),
    on(ContractActions.loadContractSuccess, ContractActions.loadContractFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingDetail: false,
    }))),

    // Save contract
    on(ContractManagementActions.createContract, ContractActions.updateContract, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingDetail: true,
    }))),
    on(ContractManagementActions.createContractSuccess, ContractManagementActions.createContractFailure,
       ContractActions.updateContractSuccess, ContractActions.updateContractFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingDetail: false,
    }))),

    // Delete contract
    on(ContractManagementActions.deleteContract, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingDetail: true,
    }))),
    on(ContractManagementActions.deleteContractSuccess, ContractManagementActions.deleteContractFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingDetail: false,
    }))),

    // Sections save
    on(ContractActions.createSection, ContractActions.updateSection, ContractActions.setSectionCatalogs, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingSections: true,
    }))),
    on(ContractActions.createSectionSuccess, ContractActions.createSectionFailure,
       ContractActions.updateSectionSuccess, ContractActions.updateSectionFailure,
       ContractActions.setSectionCatalogsSuccess, ContractActions.setSectionCatalogsFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingSections: false,
    }))),

    // Sections delete
    on(ContractActions.deleteSection, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingSections: true,
    }))),
    on(ContractActions.deleteSectionSuccess, ContractActions.deleteSectionFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingSections: false,
    }))),

    // Files upload
    on(ContractActions.uploadFiles, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        uploadingFiles: true,
    }))),
    on(ContractActions.uploadFilesSuccess, ContractActions.uploadFilesFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        uploadingFiles: false,
    }))),

    // Files delete
    on(ContractActions.deleteFile, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingFiles: true,
    }))),
    on(ContractActions.deleteFileSuccess, ContractActions.deleteFileFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingFiles: false,
    }))),

    // Reminders load
    on(ContractActions.loadReminders, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingReminders: true,
    }))),
    on(ContractActions.loadRemindersSuccess, ContractActions.loadRemindersFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        loadingReminders: false,
    }))),

    // Reminders save
    on(ContractActions.createReminder, ContractActions.updateReminder, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingReminders: true,
    }))),
    on(ContractActions.createReminderSuccess, ContractActions.createReminderFailure,
       ContractActions.updateReminderSuccess, ContractActions.updateReminderFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        savingReminders: false,
    }))),

    // Reminders delete
    on(ContractActions.deleteReminder, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingReminders: true,
    }))),
    on(ContractActions.deleteReminderSuccess, ContractActions.deleteReminderFailure, (state: PagesStore) => withContract(state, contractState => ({
        ...contractState,
        deletingReminders: false,
    }))),

    // **************************************************************************
    // CONTRACTS LIST HANDLERS
    // **************************************************************************
    ...contractsReducer,
];
